'use client';

import React from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import {
    OverlayArrow as AriaOverlayArrow,
    Popover as AriaPopover,
    PopoverContext as AriaPopoverContext,
    useContextProps,
} from 'react-aria-components';
import { useSurface } from '@milanote/roma/components/base/surface/surface';
import { cx } from '@milanote/roma/utils/cx';
import { usePopoverFocusRestoration } from './hooks/usePopoverFocusRestoration';
import { PopoverContext, usePopoverScrollRef } from './hooks/usePopoverScrollRef';
import { PopoverBody, PopoverFooter, PopoverHeader, PopoverSearch } from './popover-content';
import { DialogTrigger, MenuTrigger } from './popover-trigger';
import { styles } from './popover-styles';

export type PopoverProps = AriaPopoverProps & {
    popoverClassName?: ComponentPropsWithoutRef<'div'>['className'];
    /**
     * Whether to render a pointer arrow on the popover.
     *
     * @default false
     */
    arrow?: boolean;
};
export type PopoverBaseProps = ComponentPropsWithoutRef<'div'>;

const ROMA_POPOVER_DISPLAY_NAME = 'RomaPopover';
const ROMA_POPOVER_BASE_DISPLAY_NAME = 'RomaPopoverBase';

const AriaPopoverWithScrollRef = AriaPopover as React.FC<
    AriaPopoverProps & {
        scrollRef?: React.RefObject<Element | null>;
    }
>;

/**
 * Non-functional popover chrome used by the shell to render the panel wrapper.
 */
export const PopoverBase: React.FC<PopoverBaseProps> = ({ className, children, ...props }) => (
    <div {...props} className={cx(ROMA_POPOVER_BASE_DISPLAY_NAME, styles.base, className)}>
        {children}
    </div>
);

PopoverBase.displayName = ROMA_POPOVER_BASE_DISPLAY_NAME;

/**
 * Main Popover component that manages state and context for its subcomponents.
 */
const PopoverShell: React.FC<PopoverProps> = ({ ...props }) => {
    const [contextProps] = useContextProps(props, undefined, AriaPopoverContext);
    const {
        className,
        popoverClassName,
        children,
        triggerRef,
        isOpen,
        defaultOpen,
        onOpenChange,
        placement,
        offset,
        crossOffset,
        containerPadding,
        arrow = false,
        ...restProps
    } = contextProps;
    const surface = useSurface();
    const { popoverContext, scrollRef } = usePopoverScrollRef();

    usePopoverFocusRestoration({
        triggerRef,
        isOpen,
        defaultOpen,
    });

    return (
        <PopoverContext.Provider value={popoverContext}>
            <AriaPopoverWithScrollRef
                {...restProps}
                triggerRef={triggerRef}
                isOpen={isOpen}
                defaultOpen={defaultOpen}
                onOpenChange={onOpenChange}
                placement={placement ?? 'bottom'}
                offset={offset ?? 4}
                crossOffset={crossOffset ?? 0}
                containerPadding={containerPadding ?? 24}
                scrollRef={scrollRef}
                className={(state) =>
                    cx(
                        ROMA_POPOVER_DISPLAY_NAME,
                        surface,
                        styles.shell,
                        state.isEntering && styles.motion.entering,
                        state.isExiting && styles.motion.exiting,
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            >
                {arrow && (
                    <AriaOverlayArrow className="pointer-events-none z-10">
                        <svg
                            viewBox="0 0 100 100"
                            className="size-2.5 fill-bg-primary in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180"
                        >
                            <path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" />
                        </svg>
                    </AriaOverlayArrow>
                )}
                <PopoverBase className={popoverClassName}>{children as ReactNode}</PopoverBase>
            </AriaPopoverWithScrollRef>
        </PopoverContext.Provider>
    );
};

PopoverShell.displayName = ROMA_POPOVER_DISPLAY_NAME;

export const Popover = PopoverShell as React.FC<PopoverProps> & {
    Trigger: typeof MenuTrigger;
    MenuTrigger: typeof MenuTrigger;
    DialogTrigger: typeof DialogTrigger;
    Header: typeof PopoverHeader;
    Body: typeof PopoverBody;
    Footer: typeof PopoverFooter;
    Search: typeof PopoverSearch;
    Base: typeof PopoverBase;
};

Popover.Trigger = MenuTrigger;
Popover.MenuTrigger = MenuTrigger;
Popover.DialogTrigger = DialogTrigger;
Popover.Header = PopoverHeader;
Popover.Body = PopoverBody;
Popover.Footer = PopoverFooter;
Popover.Search = PopoverSearch;
Popover.Base = PopoverBase;
